/**
 * Communications feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Communications state, events, stats + connections + meters.
 */

import {
  useCommunicationsEvents as useGenEvents,
  useCommunicationsState as useGenState,
  useCommunicationsStats as useGenStats,
} from "@shared/api/generated/communications/communications";
import {
  getConnectionsListQueryKey,
  useConnectionsGet as useGenConnectionGet,
  useConnectionsMeters as useGenConnectionMeters,
  useConnectionsList as useGenConnectionsList,
  useConnectionsUpdate as useGenConnectionUpdate,
} from "@shared/api/generated/connections/connections";
import {
  useMetersGet as useGenMeterGet,
  useMetersUpdate as useGenMeterUpdate,
} from "@shared/api/generated/meters/meters";
import type {
  CommunicationsEventsParams,
  CommunicationsStateParams,
  CommunicationsStatsParams,
  ConnectionsListParams,
} from "@shared/api/generated/model";
import { useQueryClient } from "@tanstack/react-query";

export type {
  CommunicationEventRow,
  CommunicationStateRow,
  CommunicationStats,
  Connection,
  ConnectionMeter,
  ConnectionUpdatePayload,
  MeterUpdatePayload,
} from "@shared/api/generated/model";

// ── Communications queries ──────────────────────────────────────────────────

export const useCommunicationState = (params?: {
  region?: number;
  object?: number;
  place?: number;
  limit?: number;
}) => {
  const p: CommunicationsStateParams = {
    ...(params?.region !== undefined && { region: params.region }),
    ...(params?.object !== undefined && { object: params.object }),
    ...(params?.place !== undefined && { place: params.place }),
    ...(params?.limit !== undefined && { limit: params.limit }),
  };
  return useGenState(p, { query: { staleTime: 30_000 } });
};

export const useCommunicationEvents = (params?: {
  controller?: number;
  from?: string;
  to?: string;
  limit?: number;
}) => {
  const p: CommunicationsEventsParams = {
    ...(params?.controller !== undefined && { controller: params.controller }),
    ...(params?.from && { from: params.from }),
    ...(params?.to && { to: params.to }),
    ...(params?.limit !== undefined && { limit: params.limit }),
  };
  return useGenEvents(p, { query: { staleTime: 60_000 } });
};

export const useCommunicationStats = (params?: { region?: number; object?: number }) => {
  const p: CommunicationsStatsParams = {
    ...(params?.region !== undefined && { region: params.region }),
    ...(params?.object !== undefined && { object: params.object }),
  };
  return useGenStats(p, { query: { staleTime: 30_000, refetchInterval: 30_000 } });
};

// ── Connections queries ─────────────────────────────────────────────────────

export const useConnections = (params?: { region?: number; object?: number; limit?: number }) => {
  const p: ConnectionsListParams = {
    ...(params?.region !== undefined && { region: params.region }),
    ...(params?.object !== undefined && { object: params.object }),
    ...(params?.limit !== undefined && { limit: params.limit }),
  };
  return useGenConnectionsList(p, { query: { staleTime: 30_000, refetchInterval: 30_000 } });
};

export const useConnection = (id?: number) => {
  const enabled = id !== undefined;
  return useGenConnectionGet(id ?? 0, { query: { enabled, staleTime: 60_000 } });
};

export const useConnectionMeters = (connectionId?: number) => {
  const enabled = connectionId !== undefined;
  return useGenConnectionMeters(connectionId ?? 0, { query: { enabled, staleTime: 60_000 } });
};

// ── Connections mutations ───────────────────────────────────────────────────

export const useUpdateConnection = () => {
  const qc = useQueryClient();
  return useGenConnectionUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getConnectionsListQueryKey() });
      },
    },
  });
};

// ── Meters queries & mutations ──────────────────────────────────────────────

export const useMeter = (id?: number) => {
  const enabled = id !== undefined;
  return useGenMeterGet(id ?? 0, { query: { enabled, staleTime: 60_000 } });
};

export const useUpdateMeter = () => {
  const qc = useQueryClient();
  return useGenMeterUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getConnectionsListQueryKey() });
      },
    },
  });
};
